/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { deeplinkService } from '@src/@api-services/deeplink';
import { DEEPLINK_TYPE } from '../constants';

// ─── Types ────────────────────────────────────
type DeeplinkType = '' | typeof DEEPLINK_TYPE.EVENT | typeof DEEPLINK_TYPE.SERVICE;

export interface Deeplink {
  sourceId: string | number;  
  type: typeof DEEPLINK_TYPE[keyof typeof DEEPLINK_TYPE];
  labelName: string;
  deeplink: string;
  fromDate: string;
  toDate: string;
  fromTime: string;
  toTime: string;
  businessId: string | number;
  businessName: string;
  categoryId?: string | number;
  categoryName?: string;
  eventName?: string;
  maxBookingsAccepted?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface GetDeeplinksParams {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: DeeplinkType;
  orderBy?: string;
  orderDirection?: 'ASC' | 'DESC';
  [key: string]: any;
}

interface DeeplinksState {
  data: Deeplink[];
  total: number;
  params: GetDeeplinksParams;
  selectedDeeplink: Deeplink | null;
  categoriesDropdown: { id: number; name: string }[];
  businessesDropdown: { id: number; businessName: string }[];
  loading: boolean;
  error: string | null;
}

// ─── Async Thunks ─────────────────────────────
export const getDeeplinks = createAsyncThunk(
  'deeplinks/getDeeplinks',
  async (params: GetDeeplinksParams, { rejectWithValue }) => {
    try {
      const response = await deeplinkService.getDeeplinks(params);
      return {
        params,
        data: response.data.data.data,      
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch deeplinks');
    }
  }
);

export const getDeeplink = createAsyncThunk(
  'deeplinks/getDeeplink',
  async ({ id, type }: { id: string | number; type: typeof DEEPLINK_TYPE[keyof typeof DEEPLINK_TYPE] }, { rejectWithValue }) => {
    try {
      const response = await deeplinkService.getDeeplink(id, type);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch deeplink');
    }
  }
);

export const createDeeplink = createAsyncThunk(
  'deeplinks/createDeeplink',
  async (data: any, { dispatch, getState, rejectWithValue }: any) => {
    try {
      const response = await deeplinkService.createDeeplink(data);
      const state = getState();
      await dispatch(getDeeplinks(state.deeplinks.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create deeplink');
    }
  }
);

export const updateDeeplink = createAsyncThunk(
  'deeplinks/updateDeeplink',
  async (
    { id, data, type }: { id: string | number; data: any; type: typeof DEEPLINK_TYPE[keyof typeof DEEPLINK_TYPE] },
    { dispatch, getState, rejectWithValue }: any
  ) => {
    try {
      const response = await deeplinkService.updateDeeplink(id, data, type);
      const state = getState();
      await dispatch(getDeeplinks(state.deeplinks.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update deeplink');
    }
  }
);

export const deleteDeeplink = createAsyncThunk(
  'deeplinks/deleteDeeplink',
  async ({ id, type }: { id: string | number; type: typeof DEEPLINK_TYPE[keyof typeof DEEPLINK_TYPE] }, { dispatch, getState, rejectWithValue }: any) => {
    try {
      await deeplinkService.deleteDeeplink(id, type);
      const state = getState();
      await dispatch(getDeeplinks(state.deeplinks.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete deeplink');
    }
  }
);

export const getCategoriesDropdown = createAsyncThunk(
  'deeplinks/getCategoriesDropdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await deeplinkService.getCategoriesDropdown();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch categories');
    }
  }
);

export const getBusinessesDropdown = createAsyncThunk(
  'deeplinks/getBusinessesDropdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await deeplinkService.getBusinessesDropdown();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch businesses');
    }
  }
);

// ─── Initial State ─────────────────────────────
const initialState: DeeplinksState = {
  data: [],
  total: 0,
  params: { page: 1, pageSize: 10, orderBy: 'createdAt', orderDirection: 'DESC' },
  selectedDeeplink: null,
  categoriesDropdown: [],
  businessesDropdown: [],
  loading: false,
  error: null,
};

// ─── Slice ─────────────────────────────────────
export const deeplinksSlice = createSlice({
  name: 'deeplinks',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    clearSelectedDeeplink: (state) => { state.selectedDeeplink = null; },
    setParams: (state, action) => { state.params = { ...state.params, ...action.payload }; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeeplinks.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getDeeplinks.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getDeeplinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getDeeplink.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getDeeplink.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDeeplink = action.payload;
      })
      .addCase(getDeeplink.rejected, (state) => {
        state.loading = false;
        state.selectedDeeplink = null;
      })
      .addCase(getCategoriesDropdown.fulfilled, (state, action) => {
        state.categoriesDropdown = action.payload;
      })
      .addCase(getBusinessesDropdown.fulfilled, (state, action) => {
        state.businessesDropdown = action.payload;
      });
  },
});

export const { clearError, clearSelectedDeeplink, setParams } = deeplinksSlice.actions;
export default deeplinksSlice.reducer;